/**
 * A module's assessments, for its mentor: several per module, each uploaded
 * from the blueprint CSV. Upload → the file is checked → publish. A file with
 * mistakes lists every row that can't be published and why, and the mentor
 * uploads a corrected file (or publishes just the good rows).
 *
 * kind "topic": a 1:1 student's module. kind "template": a cohort's module,
 * shared by the whole batch.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import * as tasksApi from "./moduleTasksApi";

const FONT = "Be Vietnam Pro";
const BLUE = "#0172A6";
const TYPE_LABEL = { o: "MCQ", s: "Subjective", p: "Programming" };
const DIFFICULTY = { E: "Easy", M: "Medium", H: "Hard" };

export const countsLabel = (counts = {}) =>
  ["o", "s", "p"].filter((t) => counts[t]).map((t) => `${counts[t]} ${TYPE_LABEL[t]}`).join(" · ") || "No questions";

const reportCounts = (counts = {}) => ({ o: counts.mcq, s: counts.subjective, p: counts.programming });

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";

// ─── Upload: choose → checked → publish ─────────────────────────────────────

const UploadPanel = ({ kind, moduleId, replacing, onDone, onCancel }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(replacing?.title || "");
  const [description, setDescription] = useState(replacing?.description || "");
  const [report, setReport] = useState(null);
  const [fileError, setFileError] = useState("");
  const [checking, setChecking] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");

  const choose = () => inputRef.current?.click();

  const onFile = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = "";
    if (!picked) return;
    setFile(picked);
    setReport(null);
    setFileError("");
    setError("");
    if (!title.trim()) setTitle(picked.name.replace(/\.csv$/i, ""));
    setChecking(true);
    try {
      setReport(await tasksApi.validateCsv(kind, moduleId, picked));
    } catch (e) {
      setFileError(tasksApi.errorMessage(e, "The file couldn't be checked."));
    } finally {
      setChecking(false);
    }
  };

  const publish = async (publishValidOnly) => {
    if (!title.trim()) {
      setError("Give the assessment a title.");
      return;
    }
    setPublishing(true);
    setError("");
    const fields = { file, title: title.trim(), description, publishValidOnly };
    try {
      const result = replacing
        ? await tasksApi.updateAssessment(kind, moduleId, replacing.id, fields)
        : await tasksApi.createAssessment(kind, moduleId, fields);
      onDone(result.assessment, replacing ? "updated" : "published", result.changes);
    } catch (e) {
      const data = e?.response?.data;
      if (data?.report) setReport(data.report);
      if (data?.file_error) setFileError(data.error);
      else setError(tasksApi.errorMessage(e, "Publishing failed."));
    } finally {
      setPublishing(false);
    }
  };

  const ready = report && report.invalid_count === 0;

  return (
    <Box>
      <input ref={inputRef} type="file" accept=".csv,text/csv" hidden onChange={onFile} data-testid="csv-input" />

      {replacing && (
        <Alert severity="info" sx={{ mb: 2, fontFamily: FONT }}>
          Questions are matched by question_ref: a question with the same ref and type is updated in place and
          keeps students' answers (fixing a typo or an answer key is safe). Questions missing from the new file are
          removed{replacing.answered_count > 0 ? "; any a student already answered are kept with their answers but hidden" : ""}.
          New refs are added.
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <TextField
          label="Title" size="small" value={title} onChange={(e) => setTitle(e.target.value)}
          sx={{ flex: "1 1 240px" }} inputProps={{ maxLength: 255 }}
        />
        <TextField
          label="Description (optional)" size="small" value={description}
          onChange={(e) => setDescription(e.target.value)} sx={{ flex: "2 1 320px" }}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <Button
          variant={file ? "outlined" : "contained"} onClick={choose} disabled={checking || publishing}
          startIcon={<FileUploadOutlinedIcon />} sx={{ textTransform: "none", fontFamily: FONT }}
        >
          {file ? (report?.invalid_count || fileError ? "Upload corrected file" : "Choose another file") : "Choose CSV file"}
        </Button>
        {file && <Typography sx={{ fontSize: 13, color: "#4b5563" }}>{file.name}</Typography>}
        {checking && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: 13, color: "#4b5563" }}>Checking every question…</Typography>
          </Box>
        )}
      </Box>

      {fileError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fileError} Fix the file and upload it again.
        </Alert>
      )}

      {report && (
        <>
          {ready ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              All {plural(report.valid, "question")} can be published ({countsLabel(reportCounts(report.counts))}).
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>
                {report.invalid_count} of {report.total} questions can’t be published.
              </strong>{" "}
              Fix the rows below in your file and upload it again
              {report.valid > 0 ? `, or publish only the ${plural(report.valid, "question")} that are fine.` : "."}
            </Alert>
          )}
          {report.warnings?.map((w) => (
            <Alert key={w} severity="info" sx={{ mb: 1 }}>{w}</Alert>
          ))}
          {report.invalid_count > 0 && (
            <Box sx={{ maxHeight: 320, overflow: "auto", border: "1px solid #f3d9a4", borderRadius: 1, mb: 2 }}>
              <Table size="small" stickyHeader aria-label="Questions that can't be published">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }}>Row</TableCell>
                    <TableCell sx={{ width: 160 }}>Question</TableCell>
                    <TableCell>What’s wrong</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.invalid.map((r) => (
                    <TableRow key={r.row} data-testid="invalid-row">
                      <TableCell sx={{ verticalAlign: "top", fontWeight: 600 }}>{r.row}</TableCell>
                      <TableCell sx={{ verticalAlign: "top", fontSize: 13 }}>
                        {r.ref && <Box sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.ref}</Box>}
                        <Box sx={{ color: "#4b5563", wordBreak: "break-word" }}>{r.title || <em>(no question text)</em>}</Box>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", fontSize: 13 }}>
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {r.errors.map((e) => <li key={e}>{e}</li>)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
        <Button onClick={onCancel} disabled={publishing} sx={{ textTransform: "none" }}>Cancel</Button>
        {report && !ready && report.valid > 0 && (
          <Button onClick={() => publish(true)} disabled={publishing || checking} sx={{ textTransform: "none" }}>
            Publish only the {plural(report.valid, "valid question")}
          </Button>
        )}
        <Button
          variant="contained" onClick={() => publish(false)} disabled={!ready || publishing || checking}
          startIcon={publishing ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ textTransform: "none", backgroundColor: BLUE }}
        >
          {replacing ? "Replace questions" : "Publish assessment"}
        </Button>
      </Box>
    </Box>
  );
};

// ─── One assessment in the list ────────────────────────────────────────────

const AssessmentCard = ({ kind, moduleId, assessment, onReplace, onChanged, onDeleted, notify }) => {
  const [open, setOpen] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(assessment.title);
  const [description, setDescription] = useState(assessment.description);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !questions) {
      try {
        setQuestions((await tasksApi.getAssessment(kind, moduleId, assessment.id)).questions);
      } catch (e) {
        notify(tasksApi.errorMessage(e, "Couldn't load the questions."), "error");
        setOpen(false);
      }
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { assessment: saved } = await tasksApi.updateAssessment(kind, moduleId, assessment.id, { title, description });
      setEditing(false);
      onChanged(saved);
    } catch (e) {
      notify(tasksApi.errorMessage(e, "Couldn't save."), "error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await tasksApi.deleteAssessment(kind, moduleId, assessment.id);
      setConfirming(false);
      onDeleted(assessment);
    } catch (e) {
      notify(tasksApi.errorMessage(e, "Couldn't delete."), "error");
      setBusy(false);
    }
  };

  return (
    <Box sx={{ border: "1px solid #d6e6f2", borderRadius: "10px", p: 1.5, mb: 1.5, background: "#fff" }} data-testid="assessment-card">
      {editing ? (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ flex: "1 1 200px" }} />
          <TextField size="small" label="Description" value={description} onChange={(e) => setDescription(e.target.value)} sx={{ flex: "2 1 260px" }} />
          <Button onClick={() => { setEditing(false); setTitle(assessment.title); setDescription(assessment.description); }} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={busy || !title.trim()} sx={{ textTransform: "none", backgroundColor: BLUE }}>Save</Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 15, fontFamily: FONT }}>{assessment.title}</Typography>
            {assessment.description && (
              <Typography sx={{ fontSize: 13, color: "#4b5563", mt: 0.25 }}>{assessment.description}</Typography>
            )}
            <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
              {countsLabel(assessment.counts)} · Max score {assessment.max_score}
              {assessment.answered_count > 0 && ` · ${assessment.answered_count} answered by students`}
              {assessment.source_file_name && ` · ${assessment.source_file_name}`}
              {assessment.updated_at && ` · ${formatDate(assessment.updated_at)}`}
            </Typography>
          </Box>
          <Tooltip title="Rename"><IconButton size="small" aria-label="Rename" onClick={() => setEditing(true)}><DriveFileRenameOutlineIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Replace with a new file"><IconButton size="small" aria-label="Replace file" onClick={() => onReplace(assessment)}><SwapHorizIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" aria-label="Delete" onClick={() => setConfirming(true)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title={open ? "Hide questions" : "Show questions"}>
            <IconButton size="small" aria-label="Show questions" onClick={toggle}>{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </Tooltip>
        </Box>
      )}

      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1, borderTop: "1px solid #eef2f6", pt: 1 }}>
          {!questions ? <CircularProgress size={18} /> : questions.map((q, i) => (
            <Box key={`${q.type}-${q.id}`} sx={{ display: "flex", gap: 1, alignItems: "center", py: 0.5, fontSize: 13 }}>
              <Typography sx={{ width: 28, color: "#9ca3af", fontSize: 12 }}>{i + 1}</Typography>
              <Typography sx={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.title}</Typography>
              <Chip size="small" label={TYPE_LABEL[q.type]} />
              <Chip size="small" variant="outlined" label={DIFFICULTY[q.difficulty] || q.difficulty} />
              <Typography sx={{ width: 56, textAlign: "right", fontSize: 12, color: "#6b7280" }}>{q.max_score} pts</Typography>
            </Box>
          ))}
        </Box>
      </Collapse>

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Delete “{assessment.title}”?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            Its {plural(assessment.question_count, "question")} will be removed for {kind === "template" ? "every student in the batch" : "the student"}.
            {assessment.answered_count > 0 && ` Answers students already gave to ${plural(assessment.answered_count, "question")} are kept.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={remove} disabled={busy} sx={{ textTransform: "none" }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── The manager ───────────────────────────────────────────────────────────

const AssessmentsManager = ({ open, onClose, kind, moduleId, moduleName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [upload, setUpload] = useState(null); // null | { replacing?: assessment }
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [confirmLegacy, setConfirmLegacy] = useState(false);
  const [toast, setToast] = useState(null);

  const notify = (message, severity = "success") => setToast({ message, severity });

  const load = useCallback(async () => {
    if (!moduleId) return;
    setLoading(true);
    setLoadError("");
    try {
      setData(await tasksApi.listAssessments(kind, moduleId));
    } catch (e) {
      setLoadError(tasksApi.errorMessage(e, "Couldn't load the assessments."));
    } finally {
      setLoading(false);
    }
  }, [kind, moduleId]);

  useEffect(() => {
    if (open) {
      setUpload(null);
      load();
    }
  }, [open, load]);

  const blueprint = async () => {
    try {
      await tasksApi.downloadBlueprint();
    } catch (e) {
      notify(tasksApi.errorMessage(e, "Couldn't download the blueprint."), "error");
    }
  };

  const removeLegacy = async () => {
    try {
      await tasksApi.deleteLegacyQuestions(kind, moduleId);
      setConfirmLegacy(false);
      notify("Earlier questions removed.");
      load();
    } catch (e) {
      notify(tasksApi.errorMessage(e, "Couldn't remove them."), "error");
    }
  };

  const assessments = data?.assessments || [];
  const legacy = data?.legacy || { question_count: 0, questions: [] };

  return (
    <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontFamily: FONT }}>
        <Box sx={{ flex: 1 }}>
          {upload ? (upload.replacing ? "Replace questions" : "New assessment") : "Assessments"}
          <Typography sx={{ fontSize: 13, color: "#6b7280" }}>
            {moduleName || "Module"}{kind === "template" ? " · every student in the batch" : ""}
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={() => onClose()}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ background: "#fbfdff" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Typography sx={{ flex: 1, fontSize: 13, color: "#4b5563", minWidth: 220 }}>
            One CSV per assessment, any mix of MCQ, subjective and programming questions. Start from the blueprint:
            it has the columns and an example of each type. Every question needs its correct answer
            (correct_option, sample_answer or solution): students see it once they submit theirs.
          </Typography>
          <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={blueprint} sx={{ textTransform: "none" }}>
            Download blueprint CSV
          </Button>
        </Box>

        {upload ? (
          <UploadPanel
            kind={kind}
            moduleId={moduleId}
            replacing={upload.replacing}
            onCancel={() => setUpload(null)}
            onDone={(assessment, verb, changes) => {
              setUpload(null);
              notify(changes
                ? `“${assessment.title}” updated: ${changes.updated} kept and updated, ${changes.added} added, ${changes.removed} removed.`
                : `“${assessment.title}” ${verb}: ${plural(assessment.question_count, "question")}.`);
              load();
            }}
          />
        ) : loading && !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
        ) : loadError ? (
          <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{loadError}</Alert>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
              <Typography sx={{ flex: 1, fontWeight: 600, fontFamily: FONT }}>
                {assessments.length ? plural(assessments.length, "assessment") : "No assessments yet"}
              </Typography>
              <Button
                variant="contained" startIcon={<FileUploadOutlinedIcon />} onClick={() => setUpload({})}
                sx={{ textTransform: "none", backgroundColor: BLUE }}
              >
                Upload new assessment
              </Button>
            </Box>

            {assessments.map((a) => (
              <AssessmentCard
                key={`${a.id}-${a.updated_at}`}
                kind={kind}
                moduleId={moduleId}
                assessment={a}
                notify={notify}
                onReplace={(assessment) => setUpload({ replacing: assessment })}
                onChanged={() => { notify("Saved."); load(); }}
                onDeleted={(assessment) => { notify(`“${assessment.title}” deleted.`); load(); }}
              />
            ))}

            {legacy.question_count > 0 && (
              <Box sx={{ border: "1px dashed #cbd5e1", borderRadius: "10px", p: 1.5, mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Earlier questions</Typography>
                    <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
                      {plural(legacy.question_count, "question")} added before assessments, not part of any assessment. Students still see them.
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => setLegacyOpen(!legacyOpen)} sx={{ textTransform: "none" }}>
                    {legacyOpen ? "Hide" : "Show"}
                  </Button>
                  <Button size="small" color="error" onClick={() => setConfirmLegacy(true)} sx={{ textTransform: "none" }}>
                    Remove all
                  </Button>
                </Box>
                <Collapse in={legacyOpen}>
                  {legacy.questions.map((q, i) => (
                    <Box key={`${q.type}-${q.id}`} sx={{ display: "flex", gap: 1, alignItems: "center", py: 0.5 }}>
                      <Typography sx={{ width: 28, color: "#9ca3af", fontSize: 12 }}>{i + 1}</Typography>
                      <Typography sx={{ flex: 1, fontSize: 13 }}>{q.title}</Typography>
                      <Chip size="small" label={TYPE_LABEL[q.type]} />
                    </Box>
                  ))}
                </Collapse>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <Dialog open={confirmLegacy} onClose={() => setConfirmLegacy(false)}>
        <DialogTitle>Remove the earlier questions?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            {plural(legacy.question_count, "question")} will be removed for {kind === "template" ? "every student in the batch" : "the student"}. Answers students already gave are kept.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLegacy(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={removeLegacy} sx={{ textTransform: "none" }}>Remove</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </Dialog>
  );
};

export default AssessmentsManager;
